export const MAX_SOURCE_BYTES = 64 * 1024 * 1024;
export const MAX_SOURCE_SIDE = 16_384;
export const MIN_TARGET_SIZE = 16;
export const MAX_TARGET_SIZE = 2048;

const SUPPORTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp', 'image/avif', 'image/svg+xml'];

export interface DecodedImage {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  name: string;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RasterizeOptions {
  size: number;
  mode: 'square' | 'fit';
  crop?: CropRect | null;
}

export interface RgbaImage {
  rgba: Uint8Array;
  width: number;
  height: number;
}

export class ImageDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageDecodeError';
  }
}

export function isSupportedImageFile(file: File | Blob): boolean {
  if (!file.type) return true; // let the decoder decide
  return SUPPORTED_TYPES.includes(file.type);
}

export async function decodeImage(file: File | Blob, name = 'image'): Promise<DecodedImage> {
  if (file.size === 0) throw new ImageDecodeError('The file is empty.');
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ImageDecodeError(`The file is ${(file.size / 1_048_576).toFixed(1)} MB; the limit is ${MAX_SOURCE_BYTES / 1_048_576} MB.`);
  }
  if (!isSupportedImageFile(file)) {
    throw new ImageDecodeError(`Unsupported image type "${file.type}". Use PNG, JPEG, WebP, GIF, BMP or SVG.`);
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new ImageDecodeError('The file could not be decoded as an image. It may be corrupt or in an unsupported format.');
  }
  if (bitmap.width === 0 || bitmap.height === 0) {
    bitmap.close();
    throw new ImageDecodeError('The image has no pixels.');
  }
  if (bitmap.width > MAX_SOURCE_SIDE || bitmap.height > MAX_SOURCE_SIDE) {
    bitmap.close();
    throw new ImageDecodeError(`The image is ${bitmap.width}×${bitmap.height}; the longest side must be at most ${MAX_SOURCE_SIDE} px.`);
  }
  return { bitmap, width: bitmap.width, height: bitmap.height, name: 'name' in file && typeof file.name === 'string' ? file.name : name };
}

function createCanvas(width: number, height: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export function outputDimensions(srcWidth: number, srcHeight: number, options: RasterizeOptions): { width: number; height: number; crop: CropRect } {
  const size = Math.round(Math.min(MAX_TARGET_SIZE, Math.max(MIN_TARGET_SIZE, options.size)));
  let crop: CropRect = options.crop ?? { x: 0, y: 0, width: srcWidth, height: srcHeight };
  crop = {
    x: Math.max(0, Math.min(srcWidth - 1, Math.floor(crop.x))),
    y: Math.max(0, Math.min(srcHeight - 1, Math.floor(crop.y))),
    width: Math.max(1, Math.min(srcWidth, Math.round(crop.width))),
    height: Math.max(1, Math.min(srcHeight, Math.round(crop.height))),
  };
  crop.width = Math.min(crop.width, srcWidth - crop.x);
  crop.height = Math.min(crop.height, srcHeight - crop.y);
  if (options.mode === 'square') {
    const side = Math.min(crop.width, crop.height);
    crop = { x: crop.x + Math.floor((crop.width - side) / 2), y: crop.y + Math.floor((crop.height - side) / 2), width: side, height: side };
    return { width: size, height: size, crop };
  }
  if (crop.width >= crop.height) {
    return { width: size, height: Math.max(1, Math.round((size * crop.height) / crop.width)), crop };
  }
  return { width: Math.max(1, Math.round((size * crop.width) / crop.height)), height: size, crop };
}

/** Draws a bitmap into an RGBA buffer at the requested resolution. */
export function rasterizeBitmap(bitmap: ImageBitmap, options: RasterizeOptions): RgbaImage {
  const { width, height, crop } = outputDimensions(bitmap.width, bitmap.height, options);
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  if (!ctx) throw new ImageDecodeError('2D canvas is not available in this browser.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  return { rgba: new Uint8Array(data.buffer.slice(0)), width, height };
}

export function grayToImageData(gray: Uint8Array, width: number, height: number): ImageData {
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0, j = 0; i < gray.length; i++, j += 4) {
    const g = gray[i];
    out[j] = g;
    out[j + 1] = g;
    out[j + 2] = g;
    out[j + 3] = 255;
  }
  return new ImageData(out, width, height);
}

/** Wraps RGBA bytes as a clamped array backed by a plain ArrayBuffer. */
export function toClampedRgba(rgba: Uint8Array): Uint8ClampedArray<ArrayBuffer> {
  if (rgba.buffer instanceof ArrayBuffer && rgba.byteOffset === 0 && rgba.byteLength === rgba.buffer.byteLength) {
    return new Uint8ClampedArray(rgba.buffer);
  }
  const copy = new Uint8ClampedArray(rgba.byteLength);
  copy.set(rgba);
  return copy;
}

export function rgbaToImageData(rgba: Uint8Array, width: number, height: number): ImageData {
  return new ImageData(toClampedRgba(rgba), width, height);
}

export function imageFilesFromDataTransfer(transfer: DataTransfer | null): File[] {
  if (!transfer) return [];
  const files: File[] = [];
  for (const item of Array.from(transfer.items ?? [])) {
    if (item.kind === 'file') {
      const f = item.getAsFile();
      if (f && (f.type.startsWith('image/') || f.type === '')) files.push(f);
    }
  }
  if (files.length === 0) {
    for (const f of Array.from(transfer.files ?? [])) {
      if (f.type.startsWith('image/')) files.push(f);
    }
  }
  return files;
}
